import { useState, useEffect, useRef } from "react";
import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import CustomModal from "../../Components/CustomModal";
import CustomButton from "../../Components/CustomButton";
import CustomTable from "../../Components/CustomTable";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faHourglassEnd,
  faBan,
  faTriangleExclamation,
  faCheckDouble,
  faEdit,
  faTrashAlt,
  faFilter,
  faChevronDown,
  faPlus,
  faSave,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import { faStar as faRegStar } from "@fortawesome/free-regular-svg-icons";

import "./style.css";
import CatalogNameField, { catalogNameError } from "./CatalogNameField";

const MAX_ACTIVE_PLANS = 3;
// Sensible business ceiling for the pre-signup free-search trial allowance.
// Kept in step with the backend's `max:10000` validation rule.
const MAX_FREE_SEARCH_AMOUNT = 10000;
const activationLimitMessage = "Only 3 subscription plans can be active. Deactivate a plan before activating another.";
const apiMessage = (message, fallback) => typeof message === "string" ? message
  : Object.values(message || {}).flat().find((value) => typeof value === "string") || fallback;

const TABS = [
  { key: "plans", label: "Subscription Plans" },
  { key: "credits", label: "Report Credit Costs" },
  { key: "packages", label: "Credit Packages" },
  { key: "search-packages", label: "Search Packages" },
  { key: "upgrades", label: "Plan Upgrades" },
  { key: "subscriptions", label: "Subscriptions" },
  { key: "search-credits", label: "Free Search Credits" },
  { key: "cancellations", label: "Cancellation Requests" },
];

const WORKFLOW_BADGES = {
  cancellation_review_pending: { label: "Awaiting Cancellation Review", className: "subBadgePending", icon: faClock },
  cancellation_reviewed: { label: "Cancellation Reviewed", className: "subBadgeReviewed", icon: faCheckDouble },
  refund_processing: { label: "Refund Processing", className: "subBadgePending", icon: faClock },
  refunded: { label: "Refunded", className: "subBadgeActive", icon: faCheckCircle },
  partially_refunded: { label: "Partially Refunded", className: "subBadgeReviewed", icon: faCheckCircle },
  refund_declined: { label: "Refund Declined", className: "subBadgeDanger", icon: faBan },
  refund_failed: { label: "Refund Failed", className: "subBadgeDanger", icon: faTriangleExclamation },
  refund_cancelled: { label: "Refund Cancelled", className: "subBadgeMuted", icon: faBan },
  refund_reversed: { label: "Refund Reversed", className: "subBadgeDanger", icon: faTriangleExclamation },
};

const emptyPlan = {
  slug: "",
  name: "",
  price: "",
  currency: "USD",
  subscription_credit_package_id: "",
  subscription_search_package_id: "",
  daily_search_cap: "",
  features: "",
  recommended: false,
  is_active: true,
  sort_order: 0,
};

const emptyPackage = {
  label: "",
  credits: "",
  is_active: true,
  sort_order: 0,
};

const emptySearchPackage = {
  label: "",
  searches: "",
  // A package with no count is the unlimited one. That is how a plan — and now
  // an upgrade path — says "no yearly ceiling", instead of a separate flag.
  is_unlimited: false,
  is_active: true,
  sort_order: 0,
};

/** "500 searches" or "Unlimited", for anywhere a search package is listed. */
const searchPackageAmount = (pkg) =>
  pkg?.is_unlimited ? "Unlimited" : `${pkg?.searches} searches`;

const emptyUpgradePath = {
  from_subscription_plan_id: "",
  to_subscription_plan_id: "",
  // Allowances are attached packages, the same unit plans use — never a
  // typed-in number, which could not be validated or reused.
  subscription_credit_package_id: "",
  subscription_search_package_id: "",
  is_active: true,
};

const emptyManualUpgrade = {
  plan_id: "",
  reason: "",
  credits_granted: "",
  searches_granted: "",
};

// Remember which tab the admin was on so a page refresh keeps them there
// instead of snapping back to "Subscription Plans".
const ACTIVE_TAB_STORAGE_KEY = "subscriptionManagementActiveTab";
const isKnownTab = (key) => TABS.some((tab) => tab.key === key);
const readStoredTab = () => {
  try {
    const stored = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
    return isKnownTab(stored) ? stored : "plans";
  } catch (e) {
    return "plans";
  }
};

const SubscriptionManagement = () => {
  const [activeTab, setActiveTab] = useState(readStoredTab);

  // Persist the current tab so a refresh restores it.
  useEffect(() => {
    try {
      localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
    } catch (e) {
      /* storage unavailable — tab persistence is a convenience, not critical */
    }
  }, [activeTab]);

  const token = localStorage.getItem("login") || "";
  const apiUrl = process.env.REACT_APP_BASE_URL;
  const toastId = useRef(null);

  const showToast = (type, message) => {
    if (toastId.current) toast.dismiss(toastId.current);
    toastId.current = toast[type](message, { autoClose: 2500 });
  };

  const showLoader = () => document.querySelector(".loaderBox")?.classList.remove("d-none");
  const hideLoader = () => document.querySelector(".loaderBox")?.classList.add("d-none");

  const authHeaders = {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  /* ---------------------------- Plans ---------------------------- */
  const [plans, setPlans] = useState([]);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [planForm, setPlanForm] = useState(emptyPlan);
  const [savingPlan, setSavingPlan] = useState(false);
  const planWritePending = useRef(false);
  const activePlanCount = plans.filter((plan) => plan.is_active).length;
  const activationLimitReached = activePlanCount >= MAX_ACTIVE_PLANS;

  const fetchPlans = async () => {
    showLoader();
    try {
      const res = await fetch(`${apiUrl}/admin/subscription-plans`, { headers: authHeaders });
      const data = await res.json();
      if (data?.status) setPlans(data.data || []);
    } catch (e) {
      showToast("error", "Could not load plans");
    } finally {
      hideLoader();
    }
  };

  const openAddPlan = () => {
    setEditingPlanId(null);
    setPlanForm({ ...emptyPlan, is_active: !activationLimitReached });
    setShowPlanModal(true);
    // Refresh the package dropdowns silently — the full-screen loader would
    // otherwise flash dark behind the modal as it opens. The selects fill
    // instantly from state already in memory.
    fetchPackages(true);
    fetchSearchPackages(true);
  };

  const openEditPlan = (plan) => {
    setEditingPlanId(plan.id);
    setPlanForm({
      ...plan,
      subscription_credit_package_id: plan.subscription_credit_package_id ?? "",
      subscription_search_package_id: plan.subscription_search_package_id ?? "",
      daily_search_cap: plan.daily_search_cap ?? "",
      features: (plan.features || []).join(", "),
    });
    setShowPlanModal(true);
  };

  // Quick toggle Recommended directly from table
  const togglePlanRecommended = async (plan) => {
    const updatedVal = !plan.recommended;
    setPlans((prev) =>
      prev.map((p) => (p.id === plan.id ? { ...p, recommended: updatedVal } : p))
    );
    try {
      const res = await fetch(`${apiUrl}/admin/subscription-plans/update/${plan.id}`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ recommended: updatedVal }),
      });
      const data = await res.json();
      if (!data?.status) throw new Error(data?.message || "Failed to update recommendation");
      showToast("success", updatedVal ? `"${plan.name}" marked as recommended` : `"${plan.name}" recommendation removed`);
      fetchPlans();
    } catch (err) {
      showToast("error", err.message);
      fetchPlans();
    }
  };

  // Quick toggle Active directly from table
  const togglePlanActive = async (plan) => {
    if (planWritePending.current) return;
    const updatedVal = !plan.is_active;
    if (updatedVal && activationLimitReached) {
      showToast("error", activationLimitMessage);
      return;
    }
    planWritePending.current = true;
    setSavingPlan(true);
    try {
      const res = await fetch(`${apiUrl}/admin/subscription-plans/update/${plan.id}`, {
        method: "POST", headers: authHeaders, body: JSON.stringify({ is_active: updatedVal }),
      });
      const data = await res.json();
      if (!data?.status) throw new Error(apiMessage(data?.message, "Failed to update status"));
      showToast("success", updatedVal ? `"${plan.name}" activated` : `"${plan.name}" deactivated`);
    } catch (err) {
      showToast("error", err.message);
    } finally {
      // Reflect the committed server result; do not show a plan as active
      // while another admin may still win the final available slot.
      await fetchPlans();
      planWritePending.current = false;
      setSavingPlan(false);
    }
  };

  const submitPlan = async (e) => {
    e.preventDefault();
    if (planWritePending.current) return;
    const nameError = catalogNameError(planForm.name);
    if (nameError) { showToast("error", nameError); return; }
    const alreadyActive = plans.find((plan) => plan.id === editingPlanId)?.is_active;
    if (planForm.is_active && !alreadyActive && activationLimitReached) {
      showToast("error", activationLimitMessage);
      return;
    }
    if (!planForm.slug || !planForm.name || planForm.price === "") {
      showToast("error", "Please fill all required fields");
      return;
    }
    if (!planForm.subscription_credit_package_id) {
      showToast("error", "Please attach a credit package to this plan");
      return;
    }

    if (!planForm.subscription_search_package_id) {
      showToast("error", "Please attach a search package — pick \"Unlimited Searches\" if the plan has no yearly ceiling");
      return;
    }

    const payload = {
      slug: planForm.slug,
      name: planForm.name.trim(),
      price: parseFloat(planForm.price),
      currency: planForm.currency || "USD",
      subscription_credit_package_id: parseInt(planForm.subscription_credit_package_id, 10),
      // Unlimited is a package in this same list, so there is no second field
      // to keep in step with it.
      subscription_search_package_id: parseInt(planForm.subscription_search_package_id, 10),
      daily_search_cap:
        planForm.daily_search_cap === "" || planForm.daily_search_cap === null
          ? null
          : parseInt(planForm.daily_search_cap, 10),
      features: (planForm.features || "")
        .split(",")
        .map((f) => f.trim())
        .filter(Boolean),
      recommended: !!planForm.recommended,
      is_active: !!planForm.is_active,
      sort_order: parseInt(planForm.sort_order || 0, 10),
    };

    planWritePending.current = true;
    setSavingPlan(true);
    showLoader();
    try {
      const url = editingPlanId
        ? `${apiUrl}/admin/subscription-plans/update/${editingPlanId}`
        : `${apiUrl}/admin/subscription-plans`;
      const res = await fetch(url, { method: "POST", headers: authHeaders, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!data?.status) throw new Error(apiMessage(data?.message, "Could not save plan"));
      showToast("success", data.message || "Plan saved");
      setShowPlanModal(false);
      await fetchPlans();
      fetchPackages();
    } catch (err) {
      showToast("error", err.message);
      await fetchPlans();
    } finally {
      planWritePending.current = false;
      setSavingPlan(false);
      hideLoader();
    }
  };

  const deletePlan = async (id) => {
    showLoader();
    try {
      const res = await fetch(`${apiUrl}/admin/subscription-plans/${id}`, { method: "DELETE", headers: authHeaders });
      const data = await res.json();
      showToast(data?.status ? "success" : "error", data?.message || "Done");
      fetchPlans();
      fetchPackages();
    } catch (e) {
      showToast("error", "Could not delete plan");
    } finally {
      hideLoader();
    }
  };

  /* ---------------------- Report credit costs --------------------- */
  const [creditCosts, setCreditCosts] = useState([]);

  const fetchCreditCosts = async () => {
    showLoader();
    try {
      const res = await fetch(`${apiUrl}/admin/report-credit-costs`, { headers: authHeaders });
      const data = await res.json();
      if (data?.status) setCreditCosts(data.data || []);
    } catch (e) {
      showToast("error", "Could not load report credit costs");
    } finally {
      hideLoader();
    }
  };

  const updateCreditCostValue = (reportKey, credits) => {
    setCreditCosts((prev) => prev.map((c) => (c.report_key === reportKey ? { ...c, credits } : c)));
  };

  const saveCreditCosts = async () => {
    showLoader();
    try {
      const res = await fetch(`${apiUrl}/admin/report-credit-costs`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          costs: creditCosts.map((c) => ({
            report_key: c.report_key,
            label: c.label,
            credits: parseInt(c.credits, 10) || 0,
            is_active: true,
          })),
        }),
      });
      const data = await res.json();
      showToast(data?.status ? "success" : "error", data?.message || "Saved");
    } catch (e) {
      showToast("error", "Could not save credit costs");
    } finally {
      hideLoader();
    }
  };

  /* ------------------------ Credit packages ------------------------ */
  const [packages, setPackages] = useState([]);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [editingPackageId, setEditingPackageId] = useState(null);
  const [packageForm, setPackageForm] = useState(emptyPackage);

  const fetchPackages = async (silent = false) => {
    if (!silent) showLoader();
    try {
      const res = await fetch(`${apiUrl}/admin/credit-packages`, { headers: authHeaders });
      const data = await res.json();
      if (data?.status) setPackages(data.data || []);
    } catch (e) {
      showToast("error", "Could not load credit packages");
    } finally {
      if (!silent) hideLoader();
    }
  };

  const openAddPackage = () => {
    setEditingPackageId(null);
    setPackageForm(emptyPackage);
    setShowPackageModal(true);
  };

  const openEditPackage = (pkg) => {
    setEditingPackageId(pkg.id);
    setPackageForm({
      label: pkg.label,
      credits: pkg.credits,
      is_active: pkg.is_active,
      sort_order: pkg.sort_order,
    });
    setShowPackageModal(true);
  };

  // Quick toggle Active directly from table for packages
  const togglePackageActive = async (pkg) => {
    const updatedVal = !pkg.is_active;
    setPackages((prev) =>
      prev.map((p) => (p.id === pkg.id ? { ...p, is_active: updatedVal } : p))
    );
    try {
      const res = await fetch(`${apiUrl}/admin/credit-packages/update/${pkg.id}`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ is_active: updatedVal }),
      });
      const data = await res.json();
      if (!data?.status) throw new Error(data?.message || "Failed to update package status");
      showToast("success", updatedVal ? `"${pkg.label}" activated` : `"${pkg.label}" deactivated`);
      fetchPackages();
    } catch (err) {
      showToast("error", err.message);
      fetchPackages();
    }
  };

  const submitPackage = async (e) => {
    e.preventDefault();
    const nameError = catalogNameError(packageForm.label);
    if (nameError) { showToast("error", nameError); return; }
    if (!packageForm.label || !packageForm.credits) {
      showToast("error", "Please fill all required fields");
      return;
    }

    const payload = {
      label: packageForm.label.trim(),
      credits: parseInt(packageForm.credits, 10),
      is_active: !!packageForm.is_active,
      sort_order: parseInt(packageForm.sort_order || 0, 10),
    };

    showLoader();
    try {
      const url = editingPackageId
        ? `${apiUrl}/admin/credit-packages/update/${editingPackageId}`
        : `${apiUrl}/admin/credit-packages`;
      const res = await fetch(url, { method: "POST", headers: authHeaders, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!data?.status) throw new Error(apiMessage(data?.message, "Could not save package"));
      showToast("success", data.message || "Package saved");
      setShowPackageModal(false);
      fetchPackages();
      fetchPlans();
    } catch (err) {
      showToast("error", err.message);
    } finally {
      hideLoader();
    }
  };

  const deletePackage = async (id) => {
    showLoader();
    try {
      const res = await fetch(`${apiUrl}/admin/credit-packages/${id}`, { method: "DELETE", headers: authHeaders });
      const data = await res.json();
      showToast(data?.status ? "success" : "error", data?.message || "Done");
      fetchPackages();
    } catch (e) {
      showToast("error", "Could not delete package");
    } finally {
      hideLoader();
    }
  };

  /* ------------------------ Search packages ------------------------ */
  const [searchPackages, setSearchPackages] = useState([]);
  const [showSearchPackageModal, setShowSearchPackageModal] = useState(false);
  const [editingSearchPackageId, setEditingSearchPackageId] = useState(null);
  const [searchPackageForm, setSearchPackageForm] = useState(emptySearchPackage);

  const fetchSearchPackages = async (silent = false) => {
    if (!silent) showLoader();
    try {
      const res = await fetch(`${apiUrl}/admin/search-packages`, { headers: authHeaders });
      const data = await res.json();
      if (data?.status) setSearchPackages(data.data || []);
    } catch (e) {
      showToast("error", "Could not load search packages");
    } finally {
      if (!silent) hideLoader();
    }
  };

  const openAddSearchPackage = () => {
    setEditingSearchPackageId(null);
    setSearchPackageForm(emptySearchPackage);
    setShowSearchPackageModal(true);
  };

  const openEditSearchPackage = (pkg) => {
    setEditingSearchPackageId(pkg.id);
    setSearchPackageForm({
      label: pkg.label,
      searches: pkg.searches ?? "",
      is_unlimited: !!pkg.is_unlimited,
      is_active: pkg.is_active,
      sort_order: pkg.sort_order,
    });
    setShowSearchPackageModal(true);
  };

  const toggleSearchPackageActive = async (pkg) => {
    const updatedVal = !pkg.is_active;
    setSearchPackages((prev) =>
      prev.map((p) => (p.id === pkg.id ? { ...p, is_active: updatedVal } : p))
    );
    try {
      const res = await fetch(`${apiUrl}/admin/search-packages/update/${pkg.id}`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ is_active: updatedVal }),
      });
      const data = await res.json();
      if (!data?.status) throw new Error(data?.message || "Failed to update package status");
      showToast("success", updatedVal ? `"${pkg.label}" activated` : `"${pkg.label}" deactivated`);
      fetchSearchPackages();
    } catch (err) {
      showToast("error", err.message);
      fetchSearchPackages();
    }
  };

  const submitSearchPackage = async (e) => {
    e.preventDefault();
    const nameError = catalogNameError(searchPackageForm.label);
    if (nameError) { showToast("error", nameError); return; }
    if (!searchPackageForm.label || (!searchPackageForm.is_unlimited && !searchPackageForm.searches)) {
      showToast("error", "Please fill all required fields");
      return;
    }

    const payload = {
      label: searchPackageForm.label.trim(),
      // The server stores null for an unlimited package; sending both would
      // let "unlimited" and a count contradict each other.
      is_unlimited: !!searchPackageForm.is_unlimited,
      searches: searchPackageForm.is_unlimited ? null : parseInt(searchPackageForm.searches, 10),
      is_active: !!searchPackageForm.is_active,
      sort_order: parseInt(searchPackageForm.sort_order || 0, 10),
    };

    showLoader();
    try {
      const url = editingSearchPackageId
        ? `${apiUrl}/admin/search-packages/update/${editingSearchPackageId}`
        : `${apiUrl}/admin/search-packages`;
      const res = await fetch(url, { method: "POST", headers: authHeaders, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!data?.status) throw new Error(apiMessage(data?.message, "Could not save package"));
      showToast("success", data.message || "Search package saved");
      setShowSearchPackageModal(false);
      // Plans display this package's search count, so refresh them too.
      fetchSearchPackages();
      fetchPlans();
    } catch (err) {
      showToast("error", err.message);
    } finally {
      hideLoader();
    }
  };

  const deleteSearchPackage = async (id) => {
    showLoader();
    try {
      const res = await fetch(`${apiUrl}/admin/search-packages/${id}`, { method: "DELETE", headers: authHeaders });
      const data = await res.json();
      showToast(data?.status ? "success" : "error", data?.message || "Done");
      fetchSearchPackages();
    } catch (e) {
      showToast("error", "Could not delete search package");
    } finally {
      hideLoader();
    }
  };

  /* --------------------------- Plan upgrades ------------------------- */
  // Moving an existing subscriber up a plan, rather than selling them a
  // second one. The settings row is the master switch and the pricing
  // formula; the paths decide which moves exist and what each one grants.
  const [upgradeSettings, setUpgradeSettings] = useState({ enabled: false, proration_mode: "prorated" });
  const [upgradePaths, setUpgradePaths] = useState([]);
  const [upgradeHistory, setUpgradeHistory] = useState([]);
  const [showPathModal, setShowPathModal] = useState(false);
  const [editingPathId, setEditingPathId] = useState(null);
  const [pathForm, setPathForm] = useState(emptyUpgradePath);

  const fetchUpgradeSettings = async () => {
    try {
      const res = await fetch(`${apiUrl}/admin/subscription-upgrade-settings`, { headers: authHeaders });
      const data = await res.json();
      if (data?.status) setUpgradeSettings(data.data || {});
    } catch (e) {
      showToast("error", "Could not load upgrade settings");
    }
  };

  const fetchUpgradePaths = async () => {
    try {
      const res = await fetch(`${apiUrl}/admin/subscription-upgrade-paths`, { headers: authHeaders });
      const data = await res.json();
      if (data?.status) setUpgradePaths(data.data || []);
    } catch (e) {
      showToast("error", "Could not load upgrade paths");
    }
  };

  const fetchUpgradeHistory = async () => {
    try {
      const res = await fetch(`${apiUrl}/admin/subscription-upgrades`, { headers: authHeaders });
      const data = await res.json();
      if (data?.status) setUpgradeHistory(data.data?.data || []);
    } catch (e) {
      showToast("error", "Could not load upgrade history");
    }
  };

  const saveUpgradeSettings = async (e) => {
    e?.preventDefault();
    showLoader();
    try {
      const res = await fetch(`${apiUrl}/admin/subscription-upgrade-settings`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          enabled: !!upgradeSettings.enabled,
          proration_mode: upgradeSettings.proration_mode || "prorated",
        }),
      });
      const data = await res.json();
      if (!data?.status) throw new Error(typeof data?.message === "string" ? data.message : "Could not save settings");
      showToast("success", data.message || "Upgrade settings saved");
      setUpgradeSettings(data.data || upgradeSettings);
    } catch (err) {
      showToast("error", err.message);
    } finally {
      hideLoader();
    }
  };

  /**
   * Plans that cost more than the given one. Subscribers may only move up, so
   * every "move to" picker is built from this rather than from the full list —
   * the server refuses the wrong direction either way, but an admin should not
   * be able to choose it in the first place.
   */
  const selectedSearchPackage = (packageId) =>
    searchPackages.find((pkg) => String(pkg.id) === String(packageId));

  const dearerThan = (planId) => {
    const source = plans.find((p) => String(p.id) === String(planId));
    if (!source) return [];
    return plans.filter((p) => Number(p.price) > Number(source.price));
  };

  /**
   * The complimentary-upgrade destinations for a subscriber on `fromPlanId`,
   * driven by the Plan Upgrades matrix rather than raw price. Each entry pairs
   * the destination plan with the configured path, so the modal can both list
   * the plan and auto-fill the credits/searches that path grants. Only active
   * paths that point at an active plan are offered.
   */
  const upgradeOptionsFor = (fromPlanId) => {
    if (!fromPlanId) return [];
    return upgradePaths
      .filter((path) => path.is_active && String(path.from_subscription_plan_id) === String(fromPlanId))
      .map((path) => {
        const plan = plans.find((p) => String(p.id) === String(path.to_subscription_plan_id));
        return plan && plan.is_active ? { plan, path } : null;
      })
      .filter(Boolean)
      .sort((a, b) => Number(a.plan.price) - Number(b.plan.price));
  };

  /** The configured path for the plan currently chosen in the manual-upgrade modal. */
  const selectedManualUpgrade = () =>
    upgradeOptionsFor(manualUpgradeTarget?.subscription_plan_id)
      .find((option) => String(option.plan.id) === String(manualUpgradeForm.plan_id));

  /**
   * Whether a subscriber can be given a complimentary upgrade, and if not, the
   * reason — so the row button, its tooltip, the click guard and the modal all
   * tell the admin the same thing.
   */
  const manualUpgradeAvailability = (sub) => {
    if (!sub?.has_active_access) {
      return { allowed: false, reason: "Only an active subscription can be upgraded." };
    }
    if (upgradeOptionsFor(sub.subscription_plan_id).length > 0) {
      return { allowed: true, reason: "Move this subscriber to a higher plan at no charge." };
    }
    if (dearerThan(sub.subscription_plan_id).length === 0) {
      return { allowed: false, reason: "Already on the highest plan — there is nothing to upgrade to." };
    }
    return { allowed: false, reason: "No upgrade paths are configured from this plan. Add one in the Plan Upgrades tab first." };
  };

  const openAddPath = () => {
    setEditingPathId(null);
    setPathForm(emptyUpgradePath);
    setShowPathModal(true);
  };

  const openEditPath = (path) => {
    setEditingPathId(path.id);
    setPathForm({
      from_subscription_plan_id: path.from_subscription_plan_id ?? "",
      to_subscription_plan_id: path.to_subscription_plan_id ?? "",
      subscription_credit_package_id: path.subscription_credit_package_id ?? "",
      subscription_search_package_id: path.subscription_search_package_id ?? "",
      is_active: !!path.is_active,
    });
    setShowPathModal(true);
  };

  /**
   * The allowance difference between the two selected plans.
   *
   * Shown as a hint so an admin can see what package the move is naturally
   * worth before picking one. Deliberately not auto-filled — what an upgrade
   * grants is a commercial decision, and a prorated price with a full year of
   * credits is a reasonable thing to choose on purpose.
   */
  const allowanceGap = (key) => {
    const from = plans.find((p) => String(p.id) === String(pathForm.from_subscription_plan_id));
    const to = plans.find((p) => String(p.id) === String(pathForm.to_subscription_plan_id));
    if (!from || !to) return null;
    const gap = Number(to[key] ?? 0) - Number(from[key] ?? 0);
    return gap > 0 ? gap : null;
  };

  const submitPath = async () => {
    if (!pathForm.from_subscription_plan_id || !pathForm.to_subscription_plan_id) {
      showToast("error", "Pick the plan being upgraded from and the plan being upgraded to");
      return;
    }
    if (String(pathForm.from_subscription_plan_id) === String(pathForm.to_subscription_plan_id)) {
      showToast("error", "A plan cannot be an upgrade of itself");
      return;
    }

    const destination = plans.find((p) => String(p.id) === String(pathForm.to_subscription_plan_id));
    if (
      !pathForm.subscription_credit_package_id &&
      !pathForm.subscription_search_package_id &&
      !destination?.search_unlimited
    ) {
      showToast("error", "Attach a credit or search package — otherwise this upgrade grants nothing");
      return;
    }

    showLoader();
    try {
      const url = editingPathId
        ? `${apiUrl}/admin/subscription-upgrade-paths/update/${editingPathId}`
        : `${apiUrl}/admin/subscription-upgrade-paths`;
      const res = await fetch(url, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          from_subscription_plan_id: parseInt(pathForm.from_subscription_plan_id, 10),
          to_subscription_plan_id: parseInt(pathForm.to_subscription_plan_id, 10),
          subscription_credit_package_id: pathForm.subscription_credit_package_id
            ? parseInt(pathForm.subscription_credit_package_id, 10)
            : null,
          subscription_search_package_id: pathForm.subscription_search_package_id
            ? parseInt(pathForm.subscription_search_package_id, 10)
            : null,
          is_active: !!pathForm.is_active,
        }),
      });
      const data = await res.json();
      if (!data?.status) throw new Error(typeof data?.message === "string" ? data.message : "Could not save upgrade path");
      showToast("success", data.message || "Upgrade path saved");
      setShowPathModal(false);
      fetchUpgradePaths();
    } catch (err) {
      showToast("error", err.message);
    } finally {
      hideLoader();
    }
  };

  const toggleUpgradePathActive = async (path) => {
    const updatedVal = !path.is_active;
    setUpgradePaths((prev) => prev.map((p) => (p.id === path.id ? { ...p, is_active: updatedVal } : p)));
    try {
      const res = await fetch(`${apiUrl}/admin/subscription-upgrade-paths/update/${path.id}`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          from_subscription_plan_id: path.from_subscription_plan_id,
          to_subscription_plan_id: path.to_subscription_plan_id,
          subscription_credit_package_id: path.subscription_credit_package_id,
          subscription_search_package_id: path.subscription_search_package_id,
          is_active: updatedVal,
        }),
      });
      const data = await res.json();
      if (!data?.status) throw new Error(data?.message || "Failed to update path");
      showToast("success", updatedVal ? "Upgrade path enabled" : "Upgrade path disabled");
      fetchUpgradePaths();
    } catch (err) {
      showToast("error", err.message);
      fetchUpgradePaths();
    }
  };

  const deleteUpgradePath = async (id) => {
    showLoader();
    try {
      const res = await fetch(`${apiUrl}/admin/subscription-upgrade-paths/${id}`, { method: "DELETE", headers: authHeaders });
      const data = await res.json();
      showToast(data?.status ? "success" : "error", data?.message || "Done");
      fetchUpgradePaths();
    } catch (e) {
      showToast("error", "Could not delete upgrade path");
    } finally {
      hideLoader();
    }
  };

  /* ------------------ Complimentary upgrade (support) ---------------- */
  const [manualUpgradeTarget, setManualUpgradeTarget] = useState(null);
  const [manualUpgradeForm, setManualUpgradeForm] = useState(emptyManualUpgrade);
  const [savingManualUpgrade, setSavingManualUpgrade] = useState(false);
  const manualUpgradePending = useRef(false);

  /** null when fine, otherwise a friendly message. Blank is allowed (use the path). */
  const grantFieldError = (raw, label) => {
    const value = String(raw ?? "").trim();
    if (value === "") return null;
    if (!/^\d+$/.test(value)) return `${label} must be a whole number of 0 or more.`;
    return null;
  };

  const openManualUpgrade = (subscription) => {
    const availability = manualUpgradeAvailability(subscription);
    if (!availability.allowed) {
      showToast("error", availability.reason);
      return;
    }
    setManualUpgradeTarget(subscription);
    setManualUpgradeForm(emptyManualUpgrade);
    // The "Move To Plan" list and its auto-filled grants are read from the
    // upgrade matrix and the plan catalogue, so make sure both are current
    // when the modal opens.
    fetchUpgradePaths();
    fetchPlans();
  };

  /**
   * Picking a destination in the manual-upgrade modal pre-fills the grants from
   * that pair's configured upgrade path, exactly as a paid upgrade would apply
   * them. The admin can still override either field. Unlimited-search paths
   * leave the searches field blank — the server applies the ceiling lift from
   * the path, and a number cannot express "unlimited".
   */
  const chooseManualUpgradePlan = (planId) => {
    const option = upgradeOptionsFor(manualUpgradeTarget?.subscription_plan_id)
      .find((o) => String(o.plan.id) === String(planId));
    const path = option?.path;
    setManualUpgradeForm((prev) => ({
      ...prev,
      plan_id: planId,
      credits_granted: path ? String(path.credits_granted ?? "") : "",
      searches_granted: path && !path.grants_unlimited_search ? String(path.searches_granted ?? "") : "",
    }));
  };

  const submitManualUpgrade = async () => {
    if (manualUpgradePending.current) return;
    if (!manualUpgradeTarget) {
      showToast("error", "No subscriber selected. Please reopen the upgrade window and try again.");
      return;
    }

    // The button is disabled for inactive subscriptions, but guard here too in
    // case the row went stale while the window was open.
    if (!manualUpgradeTarget.has_active_access) {
      showToast("error", "This subscription is no longer active, so it can't be upgraded.");
      return;
    }

    if (!manualUpgradeForm.plan_id) {
      showToast("error", "Please select the plan to upgrade this subscriber to.");
      return;
    }

    const option = selectedManualUpgrade();
    if (!option) {
      showToast("error", "That plan is no longer a valid upgrade option for this subscriber. Please choose another.");
      return;
    }

    const reason = manualUpgradeForm.reason.trim();
    if (!reason) {
      showToast("error", "Please add a reason — it's saved to the upgrade history.");
      return;
    }
    if (reason.length < 3) {
      showToast("error", "Please enter a more descriptive reason (at least 3 characters).");
      return;
    }
    if (reason.length > 2000) {
      showToast("error", "The reason is too long — please keep it under 2000 characters.");
      return;
    }

    const creditsError = grantFieldError(manualUpgradeForm.credits_granted, "Credits granted");
    if (creditsError) {
      showToast("error", creditsError);
      return;
    }
    // Searches are only editable (and therefore validated) when the path is not
    // the unlimited one; an unlimited path shows a disabled "Unlimited" field.
    const isUnlimited = !!option.path?.grants_unlimited_search;
    const searchesError = isUnlimited ? null : grantFieldError(manualUpgradeForm.searches_granted, "Searches granted");
    if (searchesError) {
      showToast("error", searchesError);
      return;
    }

    manualUpgradePending.current = true;
    setSavingManualUpgrade(true);
    showLoader();
    try {
      const payload = {
        plan_id: parseInt(manualUpgradeForm.plan_id, 10),
        reason,
      };
      // Left out entirely unless set, so the configured path decides.
      if (String(manualUpgradeForm.credits_granted).trim() !== "") {
        payload.credits_granted = parseInt(manualUpgradeForm.credits_granted, 10);
      }
      if (!isUnlimited && String(manualUpgradeForm.searches_granted).trim() !== "") {
        payload.searches_granted = parseInt(manualUpgradeForm.searches_granted, 10);
      }

      const res = await fetch(`${apiUrl}/admin/subscriptions/${manualUpgradeTarget.id}/upgrade`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data?.status) throw new Error(typeof data?.message === "string" ? data.message : "Could not upgrade subscriber");
      const who = manualUpgradeTarget.user?.name || manualUpgradeTarget.user?.email || "Subscriber";
      showToast("success", data.message || `${who} upgraded to ${option.plan.name}.`);
      setManualUpgradeTarget(null);
      fetchUpgradeHistory();
    } catch (err) {
      showToast("error", err.message || "Could not upgrade subscriber. Please try again.");
    } finally {
      manualUpgradePending.current = false;
      setSavingManualUpgrade(false);
      hideLoader();
    }
  };

  /* -------------------------- Subscriptions ------------------------- */
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState("");
  const [subscriptionPage, setSubscriptionPage] = useState(1);
  const [subscriptionPages, setSubscriptionPages] = useState(1);

  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [subscriptionsError, setSubscriptionsError] = useState("");

  // Changing the status filter always returns to the first page of results.
  const changeSubscriptionFilter = (value) => {
    setSubscriptionStatusFilter(value);
    setSubscriptionPage(1);
  };

  useEffect(() => {
    if (activeTab !== "subscriptions") return;

    let disposed = false;
    let inFlight = false;
    let controller;
    setSubscriptions([]);
    setSubscriptionsError("");

    const refresh = async () => {
      if (disposed || document.visibilityState === "hidden" || inFlight) return;
      inFlight = true;
      controller = new AbortController();
      setSubscriptionsLoading(true);
      try {
        const params = new URLSearchParams({ page: String(subscriptionPage) });
        if (subscriptionStatusFilter) params.set("workflow_status", subscriptionStatusFilter);
        const res = await fetch(`${apiUrl}/admin/subscriptions?${params}`, {
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
          cache: "no-store",
          signal: controller.signal,
        });
        const data = await res.json();
        if (res.ok === false || !data?.status) throw new Error("Could not load subscriptions");
        if (!disposed) {
          // Same shape the Cancellation Requests tab reads: Laravel's paginator
          // gives the rows plus the current/last page, which keeps the control
          // in sync and self-corrects an out-of-range page after filtering.
          setSubscriptions(data.data?.data || []);
          setSubscriptionPage(data.data?.current_page || 1);
          setSubscriptionPages(data.data?.last_page || 1);
          setSubscriptionsError("");
        }
      } catch (error) {
        if (!disposed && error.name !== "AbortError") {
          setSubscriptionsError("Could not refresh subscriptions. Retrying automatically; displayed statuses may be outdated.");
        }
      } finally {
        inFlight = false;
        if (!disposed) setSubscriptionsLoading(false);
      }
    };

    refresh();
    const interval = window.setInterval(refresh, 15000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      disposed = true;
      controller?.abort();
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [activeTab, subscriptionStatusFilter, subscriptionPage, apiUrl, token]);

  /* ------------------------ Free search credits ------------------------ */
  const [searchCreditSettings, setSearchCreditSettings] = useState({ free_search_amount: 10, reset_policy: "once" });

  const fetchSearchCreditSettings = async () => {
    showLoader();
    try {
      const res = await fetch(`${apiUrl}/admin/search-credit-settings`, { headers: authHeaders });
      const data = await res.json();
      if (data?.status) setSearchCreditSettings(data.data);
    } catch (e) {
      showToast("error", "Could not load search credit settings");
    } finally {
      hideLoader();
    }
  };

  const saveSearchCreditSettings = async (e) => {
    e.preventDefault();

    // Must be a whole number within the column's unsigned-integer range, so the
    // admin gets a clear message instead of a server error on an over-large value.
    const raw = String(searchCreditSettings.free_search_amount ?? "").trim();
    if (raw === "" || !/^\d+$/.test(raw)) {
      showToast("error", "Free search amount must be a whole number of 0 or more.");
      return;
    }
    if (Number(raw) > MAX_FREE_SEARCH_AMOUNT) {
      showToast("error", `Free search amount is too large. Please enter a value of ${MAX_FREE_SEARCH_AMOUNT.toLocaleString()} or less.`);
      return;
    }

    showLoader();
    try {
      const res = await fetch(`${apiUrl}/admin/search-credit-settings`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          free_search_amount: parseInt(raw, 10),
          reset_policy: searchCreditSettings.reset_policy,
        }),
      });
      const data = await res.json();
      if (!data?.status) throw new Error(typeof data?.message === "string" ? data.message : "Could not save settings");
      setSearchCreditSettings(data.data);
      showToast("success", data.message || "Search credit settings saved");
    } catch (err) {
      showToast("error", err.message);
    } finally {
      hideLoader();
    }
  };

  /* --------------------- Cancellation requests --------------------- */
  const [cancellationRequests, setCancellationRequests] = useState([]);
  const [reviewBusyId, setReviewBusyId] = useState(null);
  const [cancellationPage, setCancellationPage] = useState(1);
  const [cancellationPages, setCancellationPages] = useState(1);

  const cancellationRequestSequence = useRef(0);
  const cancellationFetchBusy = useRef(false);
  const cancellationRefresh = useRef(null);

  const fetchCancellationRequests = async (page = cancellationPage, silent = false) => {
    if (silent && cancellationFetchBusy.current) return;
    const sequence = ++cancellationRequestSequence.current;
    cancellationFetchBusy.current = true;
    if (!silent) showLoader();
    try {
      const res = await fetch(`${apiUrl}/admin/subscription-cancellation-requests?page=${page}`, { headers: authHeaders, cache: "no-store" });
      const data = await res.json();
      if (sequence !== cancellationRequestSequence.current) return;
      if (data?.status) {
        setCancellationRequests(data.data?.data || []);
        setCancellationPage(data.data?.current_page || 1);
        setCancellationPages(data.data?.last_page || 1);
      }
    } catch (e) {
      if (!silent && sequence === cancellationRequestSequence.current) showToast("error", "Could not load cancellation requests");
    } finally {
      if (sequence === cancellationRequestSequence.current) cancellationFetchBusy.current = false;
      if (!silent) hideLoader();
    }
  };
  cancellationRefresh.current = fetchCancellationRequests;

  useEffect(() => {
    if (activeTab !== "cancellations") return;
    const refresh = () => {
      if (document.visibilityState !== "hidden") cancellationRefresh.current(cancellationPage, true);
    };
    cancellationRefresh.current(cancellationPage);
    const interval = window.setInterval(refresh, 15000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      cancellationRequestSequence.current += 1;
      cancellationFetchBusy.current = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [activeTab, cancellationPage, apiUrl, token]);

  const resolveCancellationRequest = async (id, reviewed) => {
    setReviewBusyId(id);
    showLoader();
    try {
      const res = await fetch(`${apiUrl}/admin/subscription-cancellation-requests/${id}/resolve`, { method: "POST", headers: authHeaders, body: JSON.stringify({ reviewed }) });
      const data = await res.json();
      showToast(data?.status ? "success" : "error", data?.message || "Done");
      await fetchCancellationRequests();
    } catch (e) {
      showToast("error", "Could not update request");
    } finally {
      setReviewBusyId(null);
      hideLoader();
    }
  };

  /* ------------------------ Delete confirmation ------------------------ */
  // { type: "plan" | "package" | "searchPackage" | "upgradePath", id }
  const [deleteTarget, setDeleteTarget] = useState(null);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { type, id } = deleteTarget;
    setDeleteTarget(null);
    if (type === "plan") await deletePlan(id);
    else if (type === "searchPackage") await deleteSearchPackage(id);
    else if (type === "upgradePath") await deleteUpgradePath(id);
    else await deletePackage(id);
  };

  useEffect(() => {
    fetchPlans();
    fetchCreditCosts();
    fetchPackages();
    fetchSearchPackages();
    fetchSearchCreditSettings();
    fetchUpgradeSettings();
    fetchUpgradePaths();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-pull a tab's data every time it becomes active, so a change made on one
  // tab (e.g. editing a search package) is reflected the moment you open a
  // related tab (e.g. Subscription Plans) — without needing a full page reload.
  // "subscriptions" and "cancellations" run their own polling effects below and
  // are deliberately left out here.
  useEffect(() => {
    switch (activeTab) {
      case "plans":
        fetchPlans();
        break;
      case "credits":
        fetchCreditCosts();
        break;
      case "packages":
        // Plans drive the "Attached To" column, so refresh both.
        fetchPackages();
        fetchPlans();
        break;
      case "search-packages":
        fetchSearchPackages();
        fetchPlans();
        break;
      case "upgrades":
        // The upgrade matrix and its modal read all three catalogues.
        fetchPlans();
        fetchPackages();
        fetchSearchPackages();
        fetchUpgradeSettings();
        fetchUpgradePaths();
        break;
      case "search-credits":
        fetchSearchCreditSettings();
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // History is only worth fetching when it is on screen, and it should be
  // fresh each time — a complimentary upgrade made from the Subscriptions tab
  // should show here immediately afterwards.
  useEffect(() => {
    if (activeTab !== "upgrades") return;
    fetchUpgradeHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  /* ----------------- Status Badge Render Helpers ----------------- */
  const renderSubscriptionStatus = (status, label) => {
    const workflowBadge = WORKFLOW_BADGES[status];
    if (workflowBadge) {
      return <span className={`subBadge ${workflowBadge.className}`}>
        <FontAwesomeIcon icon={workflowBadge.icon} className="badgeIcon" /> {label || workflowBadge.label}
      </span>;
    }
    switch (status) {
      case "active":
        return (
          <span className="subBadge subBadgeActive">
            <FontAwesomeIcon icon={faCheckCircle} className="badgeIcon" /> Active
          </span>
        );
      case "pending":
      case "payment_pending":
        return (
          <span className="subBadge subBadgePending">
            <FontAwesomeIcon icon={faClock} className="badgeIcon" /> Payment Pending
          </span>
        );
      case "expired":
        return (
          <span className="subBadge subBadgeExpired">
            <FontAwesomeIcon icon={faHourglassEnd} className="badgeIcon" /> Expired
          </span>
        );
      case "cancelled":
        return (
          <span className="subBadge subBadgeCancelled">
            <FontAwesomeIcon icon={faBan} className="badgeIcon" /> Cancelled
          </span>
        );
      case "payment_failed":
        return (
          <span className="subBadge subBadgeDanger">
            <FontAwesomeIcon icon={faTriangleExclamation} className="badgeIcon" /> Payment Failed
          </span>
        );
      default:
        return (
          <span className="subBadge subBadgeMuted">
            {String(status || "-").replace("_", " ")}
          </span>
        );
    }
  };

  const renderCancellationStatus = (status) => {
    const isResolved = status === "reviewed" || status === "resolved";
    if (isResolved) {
      return (
        <span className="subBadge subBadgeReviewed">
          <FontAwesomeIcon icon={faCheckDouble} className="badgeIcon" /> Reviewed
        </span>
      );
    }
    return (
      <span className="subBadge subBadgePending">
        <FontAwesomeIcon icon={faClock} className="badgeIcon" /> Pending
      </span>
    );
  };

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="row mb-3">
          <div className="col-12">
            <div className="dashCard subscriptionPage">
              <div className="row mb-3 justify-content-between align-items-center">
                <div className="col-md-6 mb-2">
                  <h2 className="mainTitle">Subscription Management</h2>
                </div>
                <div className="col-md-6 mb-2">
                  <div className="addUser justify-content-md-end">
                    {activeTab === "plans" && (
                      <CustomButton
                        text="Add Plan"
                        variant="primaryButton"
                        icon={<FontAwesomeIcon icon={faPlus} className="ms-1" />}
                        onClick={openAddPlan}
                      />
                    )}
                    {activeTab === "packages" && (
                      <CustomButton
                        text="Add Package"
                        variant="primaryButton"
                        icon={<FontAwesomeIcon icon={faPlus} className="ms-1" />}
                        onClick={openAddPackage}
                      />
                    )}
                    {activeTab === "search-packages" && (
                      <CustomButton
                        text="Add Search Package"
                        variant="primaryButton"
                        icon={<FontAwesomeIcon icon={faPlus} className="ms-1" />}
                        onClick={openAddSearchPackage}
                      />
                    )}
                    {activeTab === "upgrades" && (
                      <CustomButton
                        text="Add Upgrade Path"
                        variant="primaryButton"
                        icon={<FontAwesomeIcon icon={faPlus} className="ms-1" />}
                        onClick={openAddPath}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="subscriptionTabs mb-3">
                {TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`subscriptionTab ${activeTab === tab.key ? "active" : ""}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeTab === "plans" && (
                <p className="tabHint" role="status">
                  {activePlanCount}/{MAX_ACTIVE_PLANS} plans active. {activationLimitReached
                    ? "Deactivate a plan before activating another. New plans can be saved as inactive."
                    : "Up to 3 plans can be active at a time."}
                </p>
              )}
              {/* -------------------- PLANS TAB -------------------- */}
              {activeTab === "plans" && (
                <CustomTable
                  headers={[
                    { key: "name", title: "Name" },
                    { key: "price", title: "Price / Year" },
                    { key: "package", title: "Credit Package" },
                    { key: "searches", title: "Search Package" },
                    { key: "recommended", title: "Recommended" },
                    { key: "active", title: "Active" },
                    { key: "actions", title: "Actions" },
                  ]}
                >
                  <tbody>
                    {plans.map((plan) => (
                      <tr key={plan.id}>
                        <td className="planNameText"><span className="catalogName" title={plan.name}>{plan.name}</span></td>
                        <td className="priceText">${Number(plan.price).toFixed(2)} {plan.currency}</td>
                        <td>
                          {plan.credit_package ? (
                            <>
                              <span className="catalogName fw-semibold" title={plan.credit_package.label}>{plan.credit_package.label}</span>
                              <br />
                              <small className="text-muted">{plan.credit_allowance} credits</small>
                            </>
                          ) : (
                            <span className="subBadge subBadgeMuted">Not attached</span>
                          )}
                        </td>
                        <td>
                          {plan.search_unlimited ? (
                            <span className="subBadge subBadgeRecommended">Unlimited</span>
                          ) : plan.search_package ? (
                            <>
                              <span className="catalogName fw-semibold" title={plan.search_package.label}>{plan.search_package.label}</span>
                              <br />
                              <small className="text-muted">{plan.search_allowance} searches</small>
                            </>
                          ) : (
                            <span className="subBadge subBadgeMuted">Not attached</span>
                          )}
                          {plan.daily_search_cap ? (
                            <>
                              <br />
                              <small className="text-muted">max {plan.daily_search_cap}/day</small>
                            </>
                          ) : null}
                        </td>
                        <td>
                          <button
                            type="button"
                            className={`subBadge subBadgeInteractive ${
                              plan.recommended ? "subBadgeRecommended" : "subBadgeStandard"
                            }`}
                            onClick={() => togglePlanRecommended(plan)}
                            title={plan.recommended ? "Click to remove recommendation" : "Click to mark as recommended"}
                          >
                            <FontAwesomeIcon
                              icon={plan.recommended ? faStar : faRegStar}
                              className="badgeIcon"
                            />
                            {plan.recommended ? "Recommended" : "Standard"}
                          </button>
                        </td>
                        <td>
                          <button
                            type="button"
                            className={`subBadge subBadgeInteractive ${
                              plan.is_active ? "subBadgeActive" : "subBadgeInactive"
                            }`}
                            disabled={savingPlan || (!plan.is_active && activationLimitReached)}
                            onClick={() => togglePlanActive(plan)}
                            title={plan.is_active ? "Click to deactivate" : activationLimitReached ? activationLimitMessage : "Click to activate"}
                          >
                            <FontAwesomeIcon
                              icon={plan.is_active ? faCheckCircle : faTimesCircle}
                              className="badgeIcon"
                            />
                            {plan.is_active ? "Active" : "Inactive"}
                          </button>
                        </td>
                        <td>
                          <div className="subActionGroup">
                            <button
                              type="button"
                              className="subActionBtn subActionBtnEdit"
                              onClick={() => openEditPlan(plan)}
                              title="Edit Plan"
                            >
                              <FontAwesomeIcon icon={faEdit} className="actionIcon" /> Edit
                            </button>
                            <button
                              type="button"
                              className="subActionBtn subActionBtnDelete"
                              onClick={() => setDeleteTarget({ type: "plan", id: plan.id })}
                              title="Delete Plan"
                            >
                              <FontAwesomeIcon icon={faTrashAlt} className="actionIcon" /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {plans.length === 0 && (
                      <tr><td colSpan={7} className="text-center py-4 text-muted">No plans configured yet.</td></tr>
                    )}
                  </tbody>
                </CustomTable>
              )}

              {/* ----------------- REPORT CREDIT COSTS TAB ----------------- */}
              {activeTab === "credits" && (
                <div className="tabPanel">
                  <p className="tabHint">
                    Credits consumed per report type when a user with an active subscription generates a report.
                  </p>
                  <div className="row">
                    {creditCosts.map((cost) => (
                      <div className="col-md-4 mb-3" key={cost.report_key}>
                        <label className="mainLabel d-block fw-semibold mb-1">{cost.label}</label>
                        <input
                          type="number"
                          min="0"
                          className="themeInput"
                          value={cost.credits}
                          onChange={(e) => updateCreditCostValue(cost.report_key, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                  <CustomButton
                    text="Save Credit Costs"
                    variant="primaryButton"
                    icon={<FontAwesomeIcon icon={faSave} className="ms-1" />}
                    onClick={saveCreditCosts}
                  />
                </div>
              )}

              {/* ------------------ CREDIT PACKAGES TAB ------------------ */}
              {activeTab === "packages" && (
                <div className="tabPanel">
                  <p className="tabHint">
                    A credit package is a bundle of report credits that you attach to a subscription plan.
                    Whoever buys that plan receives exactly these credits. Packages are not sold separately —
                    the plan's yearly price is what the user pays.
                  </p>
                  <CustomTable
                    headers={[
                      { key: "label", title: "Label" },
                      { key: "credits", title: "Credits" },
                      { key: "plans", title: "Attached To" },
                      { key: "active", title: "Active" },
                      { key: "actions", title: "Actions" },
                    ]}
                  >
                    <tbody>
                      {packages.map((pkg) => (
                        <tr key={pkg.id}>
                          <td className="planNameText"><span className="catalogName" title={pkg.label}>{pkg.label}</span></td>
                          <td>
                            <span className="counterBadge">{pkg.credits}</span>
                          </td>
                          <td className="attachedPlansText">
                            {pkg.plans?.length ? (
                              pkg.plans.map((p) => p.name).join(", ")
                            ) : (
                              <span className="text-muted">Not attached to any plan</span>
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className={`subBadge subBadgeInteractive ${
                                pkg.is_active ? "subBadgeActive" : "subBadgeInactive"
                              }`}
                              onClick={() => togglePackageActive(pkg)}
                              title={pkg.is_active ? "Click to deactivate" : "Click to activate"}
                            >
                              <FontAwesomeIcon
                                icon={pkg.is_active ? faCheckCircle : faTimesCircle}
                                className="badgeIcon"
                              />
                              {pkg.is_active ? "Active" : "Inactive"}
                            </button>
                          </td>
                          <td>
                            <div className="subActionGroup">
                              <button
                                type="button"
                                className="subActionBtn subActionBtnEdit"
                                onClick={() => openEditPackage(pkg)}
                                title="Edit Package"
                              >
                                <FontAwesomeIcon icon={faEdit} className="actionIcon" /> Edit
                              </button>
                              <button
                                type="button"
                                className="subActionBtn subActionBtnDelete"
                                onClick={() => setDeleteTarget({ type: "package", id: pkg.id })}
                                disabled={!!pkg.plans?.length}
                                title={pkg.plans?.length ? "Attached to a plan — detach it first" : "Delete Package"}
                              >
                                <FontAwesomeIcon icon={faTrashAlt} className="actionIcon" /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {packages.length === 0 && (
                        <tr><td colSpan={5} className="text-center py-4 text-muted">No credit packages configured yet.</td></tr>
                      )}
                    </tbody>
                  </CustomTable>
                </div>
              )}

              {/* ----------------- SEARCH PACKAGES TAB ----------------- */}
              {activeTab === "search-packages" && (
                <div className="tabPanel">
                  <p className="tabHint">
                    A search package is a bundle of people searches you attach to a subscription plan.
                    A plan either draws its yearly search count from one of these, or is set to
                    Unlimited on the plan itself. Each search calls the paid Galaxy API, so prefer a
                    package — or set a daily cap on unlimited plans.
                  </p>
                  <CustomTable
                    headers={[
                      { key: "label", title: "Label" },
                      { key: "searches", title: "Searches" },
                      { key: "plans", title: "Attached To" },
                      { key: "active", title: "Active" },
                      { key: "actions", title: "Actions" },
                    ]}
                  >
                    <tbody>
                      {searchPackages.map((pkg) => (
                        <tr key={pkg.id}>
                          <td className="planNameText"><span className="catalogName" title={pkg.label}>{pkg.label}</span></td>
                          <td>
                            {pkg.is_unlimited ? (
                              <span className="counterBadge" title="No yearly ceiling">&infin; Unlimited</span>
                            ) : (
                              <span className="counterBadge">{pkg.searches}</span>
                            )}
                          </td>
                          <td className="attachedPlansText">
                            {pkg.plans?.length ? (
                              pkg.plans.map((p) => p.name).join(", ")
                            ) : (
                              <span className="text-muted">Not attached to any plan</span>
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className={`subBadge subBadgeInteractive ${
                                pkg.is_active ? "subBadgeActive" : "subBadgeInactive"
                              }`}
                              onClick={() => toggleSearchPackageActive(pkg)}
                              title={pkg.is_active ? "Click to deactivate" : "Click to activate"}
                            >
                              <FontAwesomeIcon
                                icon={pkg.is_active ? faCheckCircle : faTimesCircle}
                                className="badgeIcon"
                              />
                              {pkg.is_active ? "Active" : "Inactive"}
                            </button>
                          </td>
                          <td>
                            <div className="subActionGroup">
                              <button
                                type="button"
                                className="subActionBtn subActionBtnEdit"
                                onClick={() => openEditSearchPackage(pkg)}
                                title="Edit Search Package"
                              >
                                <FontAwesomeIcon icon={faEdit} className="actionIcon" /> Edit
                              </button>
                              <button
                                type="button"
                                className="subActionBtn subActionBtnDelete"
                                onClick={() => setDeleteTarget({ type: "searchPackage", id: pkg.id })}
                                disabled={!!pkg.plans?.length}
                                title={pkg.plans?.length ? "Attached to a plan — detach it first" : "Delete Search Package"}
                              >
                                <FontAwesomeIcon icon={faTrashAlt} className="actionIcon" /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {searchPackages.length === 0 && (
                        <tr><td colSpan={5} className="text-center py-4 text-muted">No search packages configured yet.</td></tr>
                      )}
                    </tbody>
                  </CustomTable>
                </div>
              )}

              {/* -------------------- PLAN UPGRADES TAB -------------------- */}
              {activeTab === "upgrades" && (
                <div className="tabPanel">
                  <p className="tabHint">
                    An upgrade moves a subscriber to a higher plan <strong>without</strong> starting a
                    second subscription. Their renewal date does not move and the credits they have
                    already used stay used — the upgrade only adds to what they hold. An upgrade
                    exists only where you create a path for it below, and only ever points at a
                    dearer plan: subscribers can move up, never down. Each path grants a credit
                    package and a search package, the same ones you attach to plans.
                  </p>

                  {/* Master switch and pricing formula */}
                  {/*
                    Top-aligned, not bottom-aligned: the hints make the columns
                    different heights, and aligning the bottoms pushed the
                    shorter ones down so the labels no longer lined up.
                  */}
                  <form onSubmit={saveUpgradeSettings} className="row align-items-start upgradeSettingsRow mb-4">
                    <div className="col-lg-4 col-md-6 mb-3">
                      <label className="mainLabel d-block fw-semibold mb-1" htmlFor="upgrades_enabled">
                        Immediate Upgrades
                      </label>
                      <select
                        id="upgrades_enabled"
                        className="themeInput"
                        value={upgradeSettings.enabled ? "1" : "0"}
                        onChange={(e) => setUpgradeSettings({ ...upgradeSettings, enabled: e.target.value === "1" })}
                      >
                        <option value="0">Off — subscribers cannot upgrade</option>
                        <option value="1">On — subscribers can upgrade mid-term</option>
                      </select>
                      <small className="fieldHint">
                        The master switch. Paths stay saved while this is off.
                      </small>
                    </div>
                    <div className="col-lg-5 col-md-6 mb-3">
                      <label className="mainLabel d-block fw-semibold mb-1" htmlFor="proration_mode">
                        How the upgrade is priced
                      </label>
                      <select
                        id="proration_mode"
                        className="themeInput"
                        value={upgradeSettings.proration_mode || "prorated"}
                        onChange={(e) => setUpgradeSettings({ ...upgradeSettings, proration_mode: e.target.value })}
                      >
                        <option value="prorated">Prorated — price difference scaled to the time left</option>
                        <option value="flat">Flat — the whole price difference, whenever they upgrade</option>
                      </select>
                      <small className="fieldHint">
                        {upgradeSettings.proration_mode === "flat"
                          ? "A subscriber upgrading on day 350 pays the same as one upgrading on day 1."
                          : "$99 → $199 with 185 of 365 days left is charged $50.68. Anything under $0.50 is granted free — Stripe cannot charge less."}
                      </small>
                    </div>
                    <div className="col-lg-3 mb-3">
                      {/* An invisible copy of the labels beside it, so the
                          button lands exactly level with the two selects
                          without hard-coding a height that would drift if the
                          label type ever changed. */}
                      <label className="mainLabel d-block fw-semibold mb-1 upgradeSettingsSpacer" aria-hidden="true">
                        &nbsp;
                      </label>
                      <CustomButton
                        type="submit"
                        text="Save Settings"
                        variant="primaryButton"
                        icon={<FontAwesomeIcon icon={faSave} className="ms-1" />}
                      />
                    </div>
                  </form>

                  {!upgradeSettings.enabled && (
                    <p className="upgradeWarning">
                      <FontAwesomeIcon icon={faTriangleExclamation} className="upgradeWarningIcon" />
                      <span>
                        Upgrades are currently switched off. The paths below are saved but no
                        subscriber can buy one until you turn them on.
                      </span>
                    </p>
                  )}

                  <h3 className="mainTitle fs-5 mb-2">Upgrade Paths</h3>
                  <CustomTable
                    headers={[
                      { key: "from", title: "From Plan" },
                      { key: "to", title: "To Plan" },
                      { key: "difference", title: "Price Difference" },
                      { key: "credits", title: "Credits Granted" },
                      { key: "searches", title: "Searches Granted" },
                      { key: "active", title: "Active" },
                      { key: "actions", title: "Actions" },
                    ]}
                  >
                    <tbody>
                      {upgradePaths.map((path) => {
                        const difference =
                          Number(path.to_plan?.price ?? 0) - Number(path.from_plan?.price ?? 0);
                        return (
                          <tr key={path.id}>
                            <td className="planNameText"><span className="catalogName" title={path.from_plan?.name || "Deleted plan"}>{path.from_plan?.name || "Deleted plan"}</span></td>
                            <td className="planNameText"><span className="catalogName" title={path.to_plan?.name || "Deleted plan"}>{path.to_plan?.name || "Deleted plan"}</span></td>
                            <td>
                              {difference > 0 ? `$${difference.toFixed(2)}` : (
                                <span className="text-muted" title="The destination plan is not dearer, so there is nothing to charge">
                                  No charge
                                </span>
                              )}
                            </td>
                            <td>
                              {path.credit_package ? (
                                <>
                                  <span className="counterBadge">+{path.credits_granted}</span>
                                  <br />
                                  <small className="catalogName text-muted" title={path.credit_package.label}>{path.credit_package.label}</small>
                                </>
                              ) : (
                                <span className="text-muted">No extra credits</span>
                              )}
                            </td>
                            <td>
                              {path.grants_unlimited_search ? (
                                <span className="counterBadge" title="This upgrade lifts the search ceiling entirely">&infin; Unlimited</span>
                              ) : path.search_package ? (
                                <>
                                  <span className="counterBadge">+{path.searches_granted}</span>
                                  <br />
                                  <small className="catalogName text-muted" title={path.search_package.label}>{path.search_package.label}</small>
                                </>
                              ) : (
                                <span className="text-muted">No extra searches</span>
                              )}
                            </td>
                            <td>
                              <button
                                type="button"
                                className={`subBadge subBadgeInteractive ${path.is_active ? "subBadgeActive" : "subBadgeInactive"}`}
                                onClick={() => toggleUpgradePathActive(path)}
                                title={path.is_active ? "Click to disable this upgrade" : "Click to enable this upgrade"}
                              >
                                <FontAwesomeIcon icon={path.is_active ? faCheckCircle : faTimesCircle} className="badgeIcon" />
                                {path.is_active ? "Active" : "Inactive"}
                              </button>
                            </td>
                            <td>
                              <div className="subActionGroup">
                                <button type="button" className="subActionBtn subActionBtnEdit" onClick={() => openEditPath(path)} title="Edit Upgrade Path">
                                  <FontAwesomeIcon icon={faEdit} className="actionIcon" /> Edit
                                </button>
                                <button
                                  type="button"
                                  className="subActionBtn subActionBtnDelete"
                                  onClick={() => setDeleteTarget({ type: "upgradePath", id: path.id })}
                                  title="Delete Upgrade Path"
                                >
                                  <FontAwesomeIcon icon={faTrashAlt} className="actionIcon" /> Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {upgradePaths.length === 0 && (
                        <tr>
                          <td colSpan={7} className="text-center py-4 text-muted">
                            No upgrade paths yet — subscribers have nothing to upgrade to. Use “Add Upgrade Path” above.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </CustomTable>

                  <h3 className="mainTitle fs-5 mt-4 mb-2">Upgrade History</h3>
                  <p className="tabHint">
                    Every completed plan change, including complimentary ones made from the
                    Subscriptions tab. Kept even if the path it used is later deleted.
                  </p>
                  <CustomTable
                    headers={[
                      { key: "user", title: "User" },
                      { key: "change", title: "Change" },
                      { key: "charged", title: "Charged" },
                      { key: "granted", title: "Granted" },
                      { key: "by", title: "By" },
                      { key: "when", title: "When" },
                    ]}
                  >
                    <tbody>
                      {upgradeHistory.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <div className="userInfoBlock">
                              <div className="userName">{row.user?.name || "Anonymous User"}</div>
                              <div className="userEmail">{row.user?.email}</div>
                            </div>
                          </td>
                          <td className="planNameText"><span className="catalogName" title={`${row.from_plan_name} → ${row.to_plan_name}`}>{row.from_plan_name} → {row.to_plan_name}</span></td>
                          <td>
                            {Number(row.amount_charged) > 0 ? (
                              <>
                                {row.currency} {Number(row.amount_charged).toFixed(2)}
                                <br />
                                <small className="text-muted">{row.proration_mode}</small>
                              </>
                            ) : (
                              <span className="text-muted">No charge</span>
                            )}
                          </td>
                          <td>
                            <span className="counterBadge">+{row.credits_granted} credits</span>
                            {row.granted_unlimited_search ? (
                              <><br /><small className="text-muted">unlimited searches</small></>
                            ) : row.searches_granted > 0 ? (
                              <><br /><small className="text-muted">+{row.searches_granted} searches</small></>
                            ) : null}
                          </td>
                          <td>
                            {row.initiated_by === "admin" ? (
                              <>
                                <span className="subBadge subBadgeReviewed">
                                  <FontAwesomeIcon icon={faLock} className="badgeIcon" /> Admin
                                </span>
                                <br />
                                <small className="text-muted" title={row.reason}>
                                  {row.admin?.name || "Admin"}: {row.reason}
                                </small>
                              </>
                            ) : (
                              <span className="subBadge subBadgeActive">
                                <FontAwesomeIcon icon={faCheckCircle} className="badgeIcon" /> Subscriber
                              </span>
                            )}
                          </td>
                          <td>{row.completed_at ? new Date(row.completed_at).toLocaleString() : "-"}</td>
                        </tr>
                      ))}
                      {upgradeHistory.length === 0 && (
                        <tr><td colSpan={6} className="text-center py-4 text-muted">No upgrades yet.</td></tr>
                      )}
                    </tbody>
                  </CustomTable>
                </div>
              )}

              {/* ------------------ SUBSCRIPTIONS TAB ------------------ */}
              {activeTab === "subscriptions" && (
                <div className="tabPanel">
                  <div className="filterWrapper">
                    <label className="filterLabel">
                      <FontAwesomeIcon icon={faFilter} className="me-2 text-muted" /> Filter By:
                    </label>
                    <div className="filterSelectBox">
                      <select
                        className="filterSelect"
                        value={subscriptionStatusFilter}
                        aria-label="Subscription status"
                        onChange={(e) => changeSubscriptionFilter(e.target.value)}
                      >
                        <option value="">All Statuses</option>
                        <option value="active">Active</option>
                        <option value="payment_pending">Payment Pending</option>
                        <option value="expired">Expired</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="payment_failed">Payment Failed</option>
                        {Object.entries(WORKFLOW_BADGES).map(([value, badge]) => <option key={value} value={value}>{badge.label}</option>)}
                      </select>
                      <FontAwesomeIcon icon={faChevronDown} className="filterSelectArrow" />
                    </div>
                  </div>

                  <p className="subscriptionRefreshStatus" role="status">
                    {subscriptionsError || (subscriptionsLoading ? "Updating subscriptions…" : "Statuses refresh automatically every 15 seconds.")}
                  </p>
                  <CustomTable
                    headers={[
                      { key: "user", title: "User" },
                      { key: "plan", title: "Plan" },
                      { key: "status", title: "Status" },
                      { key: "starts", title: "Starts" },
                      { key: "expires", title: "Expires" },
                      { key: "credits", title: "Credits Used" },
                      { key: "searches", title: "Searches Used" },
                      { key: "actions", title: "Actions" },
                    ]}
                  >
                    <tbody>
                      {subscriptions.map((sub) => (
                        <tr key={sub.id}>
                          <td>
                            <div className="userInfoBlock">
                              <div className="userName">{sub.user?.name || "Anonymous User"}</div>
                              <div className="userEmail">{sub.user?.email}</div>
                            </div>
                          </td>
                          <td className="planNameText"><span className="catalogName" title={sub.plan_name_snapshot}>{sub.plan_name_snapshot}</span></td>
                          <td>
                            {renderSubscriptionStatus(sub.workflow_status || sub.status, sub.workflow_status_label)}
                            {WORKFLOW_BADGES[sub.workflow_status] && <small className="subscriptionStatusDetail">
                              Subscription: {String(sub.status || "-").replaceAll("_", " ")}
                            </small>}
                            {Number(sub.refunded_amount) > 0 && <small className="subscriptionRefundAmount">
                              {sub.currency} {Number(sub.refunded_amount).toFixed(2)} refunded
                              {sub.workflow_status === "refunded" && Number(sub.refunded_amount) < Number(sub.price_paid) && " (unused balance)"}
                            </small>}
                          </td>
                          <td>{sub.starts_at ? new Date(sub.starts_at).toLocaleDateString() : "-"}</td>
                          <td>{sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : "-"}</td>
                          <td>
                            <span className="counterBadge">{sub.credits_used} / {sub.credits_total}</span>
                          </td>
                          <td>
                            {sub.search_unlimited ? (
                              <span className="counterBadge" title="Unlimited searches on this plan">
                                {sub.search_used} / &infin;
                              </span>
                            ) : (
                              <span className="counterBadge">{sub.search_used} / {sub.search_total}</span>
                            )}
                            {sub.daily_search_cap ? (
                              <>
                                <br />
                                <small className="text-muted">max {sub.daily_search_cap}/day</small>
                              </>
                            ) : null}
                          </td>
                          <td>
                            <div className="subActionGroup">
                              <button
                                type="button"
                                className="subActionBtn subActionBtnEdit"
                                onClick={() => openManualUpgrade(sub)}
                                disabled={!manualUpgradeAvailability(sub).allowed}
                                title={manualUpgradeAvailability(sub).reason}
                              >
                                <FontAwesomeIcon icon={faStar} className="actionIcon" /> Upgrade
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {subscriptions.length === 0 && (
                        <tr><td colSpan={8} className="text-center py-4 text-muted">{subscriptionsLoading ? "Loading subscriptions…" : subscriptionsError ? "Subscriptions are currently unavailable." : "No subscriptions found."}</td></tr>
                      )}
                    </tbody>
                  </CustomTable>
                  <div className="subReviewPagination">
                    <CustomButton type="button" variant="secondaryButton" text="Previous" disabled={subscriptionPage <= 1} onClick={() => setSubscriptionPage(subscriptionPage - 1)} />
                    <span aria-live="polite">Page {subscriptionPage} of {subscriptionPages}</span>
                    <CustomButton type="button" variant="secondaryButton" text="Next" disabled={subscriptionPage >= subscriptionPages} onClick={() => setSubscriptionPage(subscriptionPage + 1)} />
                  </div>
                </div>
              )}

              {/* ---------------- FREE SEARCH CREDITS TAB ---------------- */}
              {activeTab === "search-credits" && (
                <div className="tabPanel">
                  <p className="tabHint">
                    Free search credits are given to users who do <strong>not</strong> have an active
                    subscription. Subscribers instead follow the search policy set on their plan —
                    configure that per plan in the Subscription Plans tab.
                  </p>
                  <form onSubmit={saveSearchCreditSettings} className="row align-items-end">
                    <div className="col-md-4 mb-3">
                      <label className="mainLabel d-block fw-semibold mb-1" htmlFor="free_search_amount">
                        Free Search Credits <span className="text-danger">*</span>
                      </label>
                      <input
                        id="free_search_amount"
                        className="themeInput"
                        type="number"
                        min="0"
                        max={MAX_FREE_SEARCH_AMOUNT}
                        step="1"
                        required
                        name="free_search_amount"
                        value={searchCreditSettings.free_search_amount}
                        onChange={(e) => setSearchCreditSettings({ ...searchCreditSettings, free_search_amount: e.target.value })}
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="mainLabel d-block fw-semibold mb-1" htmlFor="reset_policy">Reset Policy</label>
                      <select
                        id="reset_policy"
                        className="themeInput"
                        value={searchCreditSettings.reset_policy}
                        onChange={(e) => setSearchCreditSettings({ ...searchCreditSettings, reset_policy: e.target.value })}
                      >
                        <option value="once">Once (lifetime allowance, never resets)</option>
                        <option value="daily">Daily (resets every day)</option>
                      </select>
                    </div>
                    <div className="col-md-4 mb-3">
                      <CustomButton
                        type="submit"
                        text="Save Settings"
                        variant="primaryButton"
                        icon={<FontAwesomeIcon icon={faSave} className="ms-1" />}
                      />
                    </div>
                  </form>
                </div>
              )}

              {/* --------------- CANCELLATION REQUESTS TAB --------------- */}
              {activeTab === "cancellations" && (
                <>
                <p>Turn Reviewed on after checking the cancellation. Turn it off to return the case to pending review. Refunds and previous refund decisions remain unchanged.</p>
                <CustomTable
                  headers={[
                    { key: "user", title: "User" },
                    { key: "plan", title: "Plan" },
                    { key: "reason", title: "Reason" },
                    { key: "status", title: "Status" },
                    { key: "requested", title: "Requested" },
                    { key: "refund", title: "Refund decision" },
                    { key: "actions", title: "Reviewed" },
                  ]}
                >
                  <tbody>
                    {cancellationRequests.map((req) => (
                      <tr key={req.id}>
                        <td>
                          <div className="userInfoBlock">
                            <div className="userName">{req.user?.name || "User"}</div>
                            <div className="userEmail">{req.user?.email}</div>
                          </div>
                        </td>
                        <td className="planNameText"><span className="catalogName" title={req.subscription?.plan_name_snapshot || "-"}>{req.subscription?.plan_name_snapshot || "-"}</span></td>
                        <td className="reasonCell">
                          {req.reason || <span className="text-muted fst-italic">No reason given</span>}
                        </td>
                        <td>{renderCancellationStatus(req.status)}</td>
                        <td>{req.created_at ? new Date(req.created_at).toLocaleDateString() : "-"}</td>
                        <td className="cancellationRefundCell">
                          <div>{({ awaiting_decision: "Awaiting refund decision", pending_review: "Review required", no_balance: "No unused refundable balance", refunded: "Refund issued — see Refund History", processing: "Refund processing", declined: "Refund declined", apple: "Managed by Apple" })[req.refund_review?.refund_state] || "Purchase unavailable"}</div>
                          {req.refund_declined_reason && <small className="cancellationDeclineReason">{req.refund_declined_reason}</small>}
                          {req.refund_review?.can_refund && <small className="d-block">Unused balance: {req.refund_review.currency} {Number(req.refund_review.max_refundable_amount).toFixed(2)}</small>}
                        </td>
                        <td>
                          <label className={`subReviewToggle ${req.status === "reviewed" ? "isReviewed" : ""} ${reviewBusyId === req.id ? "isSaving" : ""} ${req.review_locked ? "isLocked" : ""}`}>
                            <span className="subReviewToggleControl">
                              <input type="checkbox" role="switch"
                                aria-label={`Reviewed cancellation ${req.id}`}
                                aria-describedby={`review-hint-${req.id}`}
                                aria-busy={reviewBusyId === req.id}
                                checked={req.status === "reviewed"}
                                disabled={reviewBusyId === req.id || Boolean(req.review_locked)}
                                onChange={(e) => resolveCancellationRequest(req.id, e.target.checked)} />
                              <span className="subReviewToggleTrack" aria-hidden="true" />
                            </span>
                            <span className="subReviewToggleText">{reviewBusyId === req.id ? "Saving…" : req.status === "reviewed" ? "Reviewed" : "Pending"}</span>
                            {req.review_locked && <FontAwesomeIcon icon={faLock} className="subReviewLockIcon" aria-hidden="true" />}
                          </label>
                          <small id={`review-hint-${req.id}`} className="subReviewHint">
                            {req.review_locked ? req.review_lock_reason || "Review is locked after a refund decision." : "Changing review does not issue or undo refunds."}
                          </small>
                        </td>
                      </tr>
                    ))}
                    {cancellationRequests.length === 0 && (
                      <tr><td colSpan={7} className="text-center py-4 text-muted">No cancellation requests yet.</td></tr>
                    )}
                  </tbody>
                </CustomTable>
                <div className="subReviewPagination">
                  <CustomButton type="button" variant="secondaryButton" text="Previous" disabled={cancellationPage <= 1} onClick={() => setCancellationPage(cancellationPage - 1)} />
                  <span aria-live="polite">Page {cancellationPage} of {cancellationPages}</span>
                  <CustomButton type="button" variant="secondaryButton" text="Next" disabled={cancellationPage >= cancellationPages} onClick={() => setCancellationPage(cancellationPage + 1)} />
                </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Plan add/edit modal */}
      <CustomModal
        show={showPlanModal}
        close={() => setShowPlanModal(false)}
        heading={editingPlanId ? "Edit Plan" : "Add Plan"}
      >
        <div className="modalForm animateModal">
          <div className="row">
            <div className="col-sm-6">
              <div className="formField">
                <label className="mainLabel" htmlFor="plan_slug">Slug<span>*</span></label>
                <input id="plan_slug" className="themeInput" name="slug" value={planForm.slug}
                  onChange={(e) => setPlanForm({ ...planForm, slug: e.target.value })} />
              </div>
            </div>
            <div className="col-sm-6">
              <CatalogNameField id="plan_name" label="Name" value={planForm.name}
                onChange={(value) => setPlanForm({ ...planForm, name: value })} />
            </div>
          </div>
          <div className="row">
            <div className="col-sm-6">
              <div className="formField">
                <label className="mainLabel" htmlFor="plan_price">Yearly Price<span>*</span></label>
                <input id="plan_price" className="themeInput" type="number" name="price" value={planForm.price}
                  onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })} />
              </div>
            </div>
            <div className="col-sm-6">
              <div className="formField">
                <label className="mainLabel" htmlFor="plan_currency">Currency</label>
                <input id="plan_currency" className="themeInput" name="currency" value={planForm.currency}
                  onChange={(e) => setPlanForm({ ...planForm, currency: e.target.value })} />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-6">
              <div className="formField">
                <label className="mainLabel" htmlFor="plan_credit_package">Credit Package<span>*</span></label>
                <select
                  id="plan_credit_package"
                  className="themeInput"
                  name="subscription_credit_package_id"
                  value={planForm.subscription_credit_package_id}
                  onChange={(e) => setPlanForm({ ...planForm, subscription_credit_package_id: e.target.value })}
                >
                  <option value="">Select a credit package</option>
                  {packages.map((pkg) => (
                      <option key={pkg.id} value={pkg.id}>
                        {pkg.label} — {pkg.credits} credits{pkg.is_active ? "" : " (inactive)"}
                      </option>
                    ))}
                </select>
                <small className="fieldHint">
                  Subscribers on this plan receive this package's credits.
                  {packages.length === 0 && " Create a package in the Credit Packages tab first."}
                </small>
              </div>
            </div>
            <div className="col-sm-6">
              <div className="formField">
                <label className="mainLabel" htmlFor="plan_search_package">Search Package<span>*</span></label>
                <select
                  id="plan_search_package"
                  className="themeInput"
                  name="subscription_search_package_id"
                  value={planForm.subscription_search_package_id}
                  onChange={(e) => setPlanForm({ ...planForm, subscription_search_package_id: e.target.value })}
                >
                  <option value="">Select a search package</option>
                  {searchPackages
                    .filter((pkg) => pkg.is_active || String(pkg.id) === String(planForm.subscription_search_package_id))
                    .map((pkg) => (
                      <option key={pkg.id} value={pkg.id}>
                        {pkg.label} — {searchPackageAmount(pkg)}{pkg.is_active ? "" : " (inactive)"}
                      </option>
                    ))}
                </select>
                <small className="fieldHint">
                  {selectedSearchPackage(planForm.subscription_search_package_id)?.is_unlimited
                    ? "Subscribers can search without a yearly ceiling. Set a daily cap below to bound API cost."
                    : "Searches included per year on this plan. Unlimited is a package too — pick it from this list."}
                </small>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-6">
              <div className="formField">
                <label className="mainLabel" htmlFor="plan_daily_cap">Daily Search Cap</label>
                <input
                  id="plan_daily_cap"
                  className="themeInput"
                  type="number"
                  min="1"
                  placeholder="No daily cap"
                  name="daily_search_cap"
                  value={planForm.daily_search_cap}
                  onChange={(e) => setPlanForm({ ...planForm, daily_search_cap: e.target.value })}
                />
                <small className="fieldHint">
                  Optional fair-use valve, applied on top of the choice above. Leave blank for none.
                  Recommended on unlimited plans — each search bills the Galaxy API per match.
                </small>
              </div>
            </div>
            <div className="col-sm-6">
              <div className="formField">
                <label className="mainLabel" htmlFor="plan_sort">Sort Order</label>
                <input id="plan_sort" className="themeInput" type="number" name="sort_order" value={planForm.sort_order}
                  onChange={(e) => setPlanForm({ ...planForm, sort_order: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="formField">
            <label className="mainLabel" htmlFor="plan_features">Features (comma separated)</label>
            <textarea id="plan_features" className="themeTextarea" rows={3} name="features" value={planForm.features}
              onChange={(e) => setPlanForm({ ...planForm, features: e.target.value })} />
          </div>

          <div className="row">
            <div className="col-sm-6">
              <div className="toggleRow">
                <span>Recommended / Most Popular</span>
                <label className="switch">
                  <input type="checkbox" checked={!!planForm.recommended}
                    onChange={(e) => setPlanForm({ ...planForm, recommended: e.target.checked })} />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
            <div className="col-sm-6">
              <div className="toggleRow">
                <span>Active (visible to users)</span>
                <label className="switch">
                  <input type="checkbox" checked={!!planForm.is_active}
                    disabled={savingPlan || (!planForm.is_active && activationLimitReached && !plans.find((plan) => plan.id === editingPlanId)?.is_active)}
                    onChange={(e) => setPlanForm({ ...planForm, is_active: e.target.checked })} />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          </div>
          {!planForm.is_active && activationLimitReached && !plans.find((plan) => plan.id === editingPlanId)?.is_active && (
            <div className="planLimitWarning">
              <FontAwesomeIcon icon={faTriangleExclamation} className="planLimitWarningIcon" />
              <span>
                You already have {MAX_ACTIVE_PLANS} active plans — the maximum allowed at a time.
                Deactivate another plan first to make this one active. You can still save it as inactive for now.
              </span>
            </div>
          )}

          <button type="button" className="submitBtn" disabled={savingPlan} onClick={submitPlan}>{savingPlan ? "Saving..." : "Save Plan"}</button>
        </div>
      </CustomModal>

      {/* Credit package add/edit modal */}
      <CustomModal
        show={showPackageModal}
        close={() => setShowPackageModal(false)}
        heading={editingPackageId ? "Edit Credit Package" : "Add Credit Package"}
      >
        <div className="modalForm animateModal">
          <CatalogNameField id="pkg_label" label="Label" value={packageForm.label}
            onChange={(value) => setPackageForm({ ...packageForm, label: value })} />
          <div className="row">
            <div className="col-sm-6">
              <div className="formField">
                <label className="mainLabel" htmlFor="pkg_credits">Credits<span>*</span></label>
                <input id="pkg_credits" className="themeInput" type="number" min="1" name="credits" value={packageForm.credits}
                  onChange={(e) => setPackageForm({ ...packageForm, credits: e.target.value })} />
                <small className="fieldHint">Report credits granted by any plan this package is attached to.</small>
              </div>
            </div>
            <div className="col-sm-6">
              <div className="formField">
                <label className="mainLabel" htmlFor="pkg_sort">Sort Order</label>
                <input id="pkg_sort" className="themeInput" type="number" name="sort_order" value={packageForm.sort_order}
                  onChange={(e) => setPackageForm({ ...packageForm, sort_order: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="toggleRow">
            <span>Active</span>
            <label className="switch">
              <input type="checkbox" checked={!!packageForm.is_active}
                onChange={(e) => setPackageForm({ ...packageForm, is_active: e.target.checked })} />
              <span className="slider"></span>
            </label>
          </div>

          <button type="button" className="submitBtn" onClick={submitPackage}>Save Package</button>
        </div>
      </CustomModal>

      {/* Search package add/edit modal */}
      <CustomModal
        show={showSearchPackageModal}
        close={() => setShowSearchPackageModal(false)}
        heading={editingSearchPackageId ? "Edit Search Package" : "Add Search Package"}
      >
        <div className="modalForm animateModal">
          <CatalogNameField id="spkg_label" label="Label" value={searchPackageForm.label}
            onChange={(value) => setSearchPackageForm({ ...searchPackageForm, label: value })} />
          <div className="row">
            <div className="col-sm-6">
              <div className="formField">
                <label className="mainLabel" htmlFor="spkg_searches">
                  Searches{searchPackageForm.is_unlimited ? "" : <span>*</span>}
                </label>
                <input id="spkg_searches" className="themeInput" type="number" min="1" name="searches"
                  disabled={!!searchPackageForm.is_unlimited}
                  placeholder={searchPackageForm.is_unlimited ? "No yearly ceiling" : ""}
                  value={searchPackageForm.is_unlimited ? "" : searchPackageForm.searches}
                  onChange={(e) => setSearchPackageForm({ ...searchPackageForm, searches: e.target.value })} />
                <small className="fieldHint">
                  {searchPackageForm.is_unlimited
                    ? "An unlimited package stores no count. Bound the cost with a daily cap on the plan instead."
                    : "People searches granted per year by any plan this package is attached to."}
                </small>
              </div>
            </div>
            <div className="col-sm-6">
              <div className="formField">
                <label className="mainLabel" htmlFor="spkg_sort">Sort Order</label>
                <input id="spkg_sort" className="themeInput" type="number" name="sort_order"
                  value={searchPackageForm.sort_order}
                  onChange={(e) => setSearchPackageForm({ ...searchPackageForm, sort_order: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="toggleRow">
            <span>Unlimited (no yearly ceiling)</span>
            <label className="switch">
              <input type="checkbox" checked={!!searchPackageForm.is_unlimited}
                onChange={(e) => setSearchPackageForm({ ...searchPackageForm, is_unlimited: e.target.checked })} />
              <span className="slider"></span>
            </label>
          </div>
          <div className="toggleRow">
            <span>Active</span>
            <label className="switch">
              <input type="checkbox" checked={!!searchPackageForm.is_active}
                onChange={(e) => setSearchPackageForm({ ...searchPackageForm, is_active: e.target.checked })} />
              <span className="slider"></span>
            </label>
          </div>

          <button type="button" className="submitBtn" onClick={submitSearchPackage}>Save Search Package</button>
        </div>
      </CustomModal>

      {/* Upgrade path add/edit modal */}
      <CustomModal
        show={showPathModal}
        close={() => setShowPathModal(false)}
        heading={editingPathId ? "Edit Upgrade Path" : "Add Upgrade Path"}
      >
        <div className="modalForm animateModal">
          <p className="fieldHint mb-3">
            Creating a path is what makes this move an upgrade. The credits and searches you set
            here are <strong>added</strong> to what the subscriber already holds — they are not a
            new total, and what they have already used is not given back.
          </p>
          <div className="row">
            <div className="col-sm-6">
              <div className="formField">
                <label className="mainLabel" htmlFor="path_from">From Plan<span>*</span></label>
                <select
                  id="path_from"
                  className="themeInput"
                  value={pathForm.from_subscription_plan_id}
                  onChange={(e) => setPathForm({ ...pathForm, from_subscription_plan_id: e.target.value })}
                >
                  <option value="">Select the plan they are on</option>
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} — ${Number(p.price).toFixed(2)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-sm-6">
              <div className="formField">
                <label className="mainLabel" htmlFor="path_to">To Plan<span>*</span></label>
                <select
                  id="path_to"
                  className="themeInput"
                  value={pathForm.to_subscription_plan_id}
                  onChange={(e) => setPathForm({ ...pathForm, to_subscription_plan_id: e.target.value })}
                >
                  <option value="">Select the plan they move to</option>
                  {dearerThan(pathForm.from_subscription_plan_id).map((p) => (
                    <option key={p.id} value={p.id}>{p.name} — ${Number(p.price).toFixed(2)}</option>
                  ))}
                </select>
                <small className="fieldHint">
                  {pathForm.from_subscription_plan_id && dearerThan(pathForm.from_subscription_plan_id).length === 0
                    ? "No plan costs more than the one selected, so there is nothing to upgrade to."
                    : "Only plans dearer than the one on the left are listed — subscribers can move up, never down. The amount charged comes from the difference between these two prices, using the formula set on the Plan Upgrades tab."}
                </small>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-6">
              <div className="formField">
                <label className="mainLabel" htmlFor="path_credit_package">Credits Granted</label>
                <select
                  id="path_credit_package"
                  className="themeInput"
                  value={pathForm.subscription_credit_package_id}
                  onChange={(e) => setPathForm({ ...pathForm, subscription_credit_package_id: e.target.value })}
                >
                  <option value="">No extra credits</option>
                  {packages.map((pkg) => (
                    <option key={pkg.id} value={pkg.id} disabled={!pkg.is_active}>
                      {pkg.label} — {pkg.credits} credits{pkg.is_active ? "" : " (inactive)"}
                    </option>
                  ))}
                </select>
                <small className="fieldHint">
                  {allowanceGap("credit_allowance")
                    ? `These plans differ by ${allowanceGap("credit_allowance")} credits — attach a package for that, or choose another deliberately. `
                    : ""}
                  Added on top of the balance the subscriber already holds. Manage the packages
                  themselves on the Credit Packages tab.
                </small>
              </div>
            </div>
            <div className="col-sm-6">
              <div className="formField">
                <label className="mainLabel" htmlFor="path_search_package">Searches Granted</label>
                <select
                  id="path_search_package"
                  className="themeInput"
                  value={pathForm.subscription_search_package_id}
                  onChange={(e) => setPathForm({ ...pathForm, subscription_search_package_id: e.target.value })}
                >
                  <option value="">No extra searches</option>
                  {searchPackages.map((pkg) => (
                    <option key={pkg.id} value={pkg.id} disabled={!pkg.is_active}>
                      {pkg.label} — {searchPackageAmount(pkg)}{pkg.is_active ? "" : " (inactive)"}
                    </option>
                  ))}
                </select>
                <small className="fieldHint">
                  {plans.find((p) => String(p.id) === String(pathForm.to_subscription_plan_id))?.search_unlimited
                    ? "The destination plan is already unlimited, so the subscriber becomes unlimited whatever is picked here."
                    : selectedSearchPackage(pathForm.subscription_search_package_id)?.is_unlimited
                      ? "This upgrade makes the subscriber unlimited, even though the destination plan is not."
                      : "Added to the searches left in the current term. Pick “Unlimited Searches” to lift the ceiling entirely."}
                </small>
              </div>
            </div>
          </div>

          <div className="toggleRow">
            <span>Active (subscribers can take this upgrade)</span>
            <label className="switch">
              <input type="checkbox" checked={!!pathForm.is_active}
                onChange={(e) => setPathForm({ ...pathForm, is_active: e.target.checked })} />
              <span className="slider"></span>
            </label>
          </div>

          <button type="button" className="submitBtn" onClick={submitPath}>Save Upgrade Path</button>
        </div>
      </CustomModal>

      {/* Complimentary upgrade for one subscriber */}
      <CustomModal
        show={!!manualUpgradeTarget}
        close={() => setManualUpgradeTarget(null)}
        heading="Upgrade Subscriber (no charge)"
      >
        <div className="modalForm animateModal">
          <p className="fieldHint mb-3">
            Moves <strong>{manualUpgradeTarget?.user?.email}</strong> from{" "}
            <strong>{manualUpgradeTarget?.plan_name_snapshot}</strong> to another plan without taking
            payment. Their renewal date and used credits are left alone. This is recorded against
            your account in the upgrade history.
          </p>
          {upgradeOptionsFor(manualUpgradeTarget?.subscription_plan_id).length === 0 ? (
            <div className="planLimitWarning" role="status">
              <FontAwesomeIcon icon={faTriangleExclamation} className="planLimitWarningIcon" />
              <span>
                {dearerThan(manualUpgradeTarget?.subscription_plan_id).length === 0
                  ? `${manualUpgradeTarget?.plan_name_snapshot || "This subscriber"} is already on the highest plan, so there is nothing to upgrade them to.`
                  : "No upgrade paths are configured from this subscriber's current plan. Add one in the Plan Upgrades tab to enable a complimentary upgrade."}
              </span>
            </div>
          ) : (
            <>
              <div className="formField">
                <label className="mainLabel" htmlFor="manual_plan">Move To Plan<span>*</span></label>
                <select
                  id="manual_plan"
                  className="themeInput"
                  value={manualUpgradeForm.plan_id}
                  onChange={(e) => chooseManualUpgradePlan(e.target.value)}
                >
                  <option value="">Select a plan</option>
                  {upgradeOptionsFor(manualUpgradeTarget?.subscription_plan_id).map(({ plan }) => (
                    <option key={plan.id} value={plan.id}>{plan.name} — ${Number(plan.price).toFixed(2)}</option>
                  ))}
                </select>
                <small className="fieldHint">
                  Only plans with a configured upgrade path from the current plan are listed, matching the Plan Upgrades section. Selecting one pre-fills the credits and searches it grants.
                </small>
              </div>
              <div className="formField">
                <label className="mainLabel" htmlFor="manual_reason">Reason<span>*</span></label>
                <textarea
                  id="manual_reason"
                  className="themeTextarea"
                  rows={3}
                  placeholder="e.g. Compensation for ticket #4821"
                  value={manualUpgradeForm.reason}
                  onChange={(e) => setManualUpgradeForm({ ...manualUpgradeForm, reason: e.target.value })}
                />
              </div>
              <div className="row">
                <div className="col-sm-6">
                  <div className="formField">
                    <label className="mainLabel" htmlFor="manual_credits">Credits Granted</label>
                    <input
                      id="manual_credits"
                      className="themeInput"
                      type="number"
                      min="0"
                      placeholder="Use the configured path"
                      value={manualUpgradeForm.credits_granted}
                      onChange={(e) => setManualUpgradeForm({ ...manualUpgradeForm, credits_granted: e.target.value })}
                    />
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="formField">
                    <label className="mainLabel" htmlFor="manual_searches">Searches Granted</label>
                    {selectedManualUpgrade()?.path?.grants_unlimited_search ? (
                      <input
                        id="manual_searches"
                        className="themeInput"
                        type="text"
                        value="Unlimited"
                        disabled
                        title="This upgrade path lifts the search ceiling entirely"
                      />
                    ) : (
                      <input
                        id="manual_searches"
                        className="themeInput"
                        type="number"
                        min="0"
                        placeholder="Use the configured path"
                        value={manualUpgradeForm.searches_granted}
                        onChange={(e) => setManualUpgradeForm({ ...manualUpgradeForm, searches_granted: e.target.value })}
                      />
                    )}
                  </div>
                </div>
              </div>
              <small className="fieldHint d-block mb-3">
                These are pre-filled from the selected plan's upgrade path. Adjust either value to grant
                something different, or leave them to apply the configured path exactly.
              </small>

              <button
                type="button"
                className="submitBtn"
                onClick={submitManualUpgrade}
                disabled={savingManualUpgrade}
              >
                {savingManualUpgrade ? "Upgrading…" : "Upgrade Subscriber"}
              </button>
            </>
          )}
        </div>
      </CustomModal>

      {/* Shared delete confirmation */}
      <CustomModal
        show={!!deleteTarget}
        close={() => setDeleteTarget(null)}
        action={confirmDelete}
        heading={
          deleteTarget?.type === "plan"
            ? "Delete this plan? This cannot be undone. A plan that has subscribers can't be deleted — deactivate it instead to hide it from new users."
            : deleteTarget?.type === "searchPackage"
              ? "Delete this search package? Packages attached to a plan cannot be deleted."
              : deleteTarget?.type === "upgradePath"
                ? "Delete this upgrade path? Subscribers will no longer be able to make this move. Upgrades already taken keep their history."
                : "Delete this credit package? Packages attached to a plan cannot be deleted."
        }
      />
    </DashboardLayout>
  );
};

export default SubscriptionManagement;
